import type { ReactNode } from 'react';
import {
  CategoryBarChart,
  ChartCard,
  DailySeriesChart,
  formatCount,
  formatCredits,
  formatUsd,
  CHART_SEMANTIC,
} from '@kioskgaming/ui';
import GaugeChart from './GaugeChart';
import ShopWalletCard from './ShopWalletCard';
import HeadlineCard from './HeadlineCard';
import MetricStrip from './MetricStrip';
import { cashSettlementView, channelMixFromKpis } from './chartHelpers';
import type { DashboardV2Period, ShopDashboardV2OverviewData } from '../../services/api';

type Props = {
  period: DashboardV2Period;
  costRate: number;
  defaultOpenCreditPurchase?: boolean;
  /** False when the page already shows the wallet card above the analytics (the Shop Dashboard at /). */
  showWalletCard?: boolean;
  data: ShopDashboardV2OverviewData | null;
  isLoading: boolean;
};

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="space-y-2">
    <h2 className="text-xs font-semibold uppercase tracking-wide text-blue-900/70">{title}</h2>
    {children}
  </section>
);

export default function OverviewTab({
  costRate,
  defaultOpenCreditPurchase,
  showWalletCard = true,
  data,
  isLoading,
}: Props) {
  const kpis = data?.kpis;
  const extras = data?.extras;
  const dailyFlow = data?.charts?.dailyPlayerFlow ?? [];
  const cash = cashSettlementView(extras?.cashSettlement);

  const activityMix = channelMixFromKpis([
    { name: 'Online deposits', kpi: kpis?.onlineDepositsNetUsd },
    { name: 'Counter deposits', kpi: kpis?.counterDepositsCashReceived },
    { name: 'Player money in', kpi: kpis?.playerMoneyIn },
  ]);

  return (
    <div className="space-y-6">
      <Section title="Headline">
        <div className="space-y-3">
          <HeadlineCard
            label="Shop net profit"
            tooltip="Player money in minus spent (credits, cash to Agent, payout funding, counter redemptions) for the selected period."
            kpi={kpis?.shopNetProfit}
            formatValue={formatUsd}
          />
          <MetricStrip
            columnsClassName="grid-cols-2 sm:grid-cols-3 xl:grid-cols-6"
            items={[
              {
                id: 'playerIn',
                label: 'Player money in',
                tooltip: 'Online deposit net + counter cash received.',
                kpi: kpis?.playerMoneyIn,
                format: 'currency',
                barClassName: 'bg-emerald-100',
                valueClassName: 'text-emerald-800',
              },
              {
                id: 'spent',
                label: 'Spent',
                tooltip: 'Credits purchased, cash paid to Agent, payout funding, and counter redemptions.',
                kpi: kpis?.spentTotal,
                format: 'currency',
                invertDelta: true,
                barClassName: 'bg-orange-100',
                valueClassName: 'text-orange-800',
              },
              {
                id: 'onlineDep',
                label: 'Online deposits',
                tooltip: 'Net USD from online Player deposits.',
                kpi: kpis?.onlineDepositsNetUsd,
                format: 'currency',
                barClassName: 'bg-emerald-100',
                valueClassName: 'text-emerald-800',
              },
              {
                id: 'counterDep',
                label: 'Counter deposits',
                tooltip: 'Cash received at the counter.',
                kpi: kpis?.counterDepositsCashReceived,
                format: 'currency',
                barClassName: 'bg-blue-100',
              },
              {
                id: 'credits',
                label: 'Credits available',
                tooltip: 'Credit balance available right now.',
                value: formatCredits(kpis?.creditAvailable?.value ?? 0),
                barClassName: 'bg-blue-100',
              },
              {
                id: 'roadblocks',
                label: 'Roadblocks',
                tooltip: 'Open blockers needing attention.',
                value: formatCount(extras?.roadblocksBadgeCount ?? 0),
                barClassName: extras?.roadblocksBadgeCount ? 'bg-orange-100' : 'bg-emerald-100',
                valueClassName: extras?.roadblocksBadgeCount ? 'text-orange-800' : 'text-emerald-800',
              },
            ]}
          />
        </div>
      </Section>

      {showWalletCard ? (
        <ShopWalletCard
          creditBalance={kpis?.creditBalance?.value ?? 0}
          creditAvailable={kpis?.creditAvailable?.value ?? 0}
          usdBalanceNet={kpis?.usdBalanceNet?.value ?? 0}
          isLoading={isLoading}
          costRate={costRate}
          defaultOpenCreditPurchase={defaultOpenCreditPurchase}
          tone="blue"
        />
      ) : null}

      <Section title="Cash flow">
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <ChartCard
            title="Player cash flow trend"
            tooltip="Player money in (online + counter) vs payouts (online withdrawals + counter redemptions)."
            loading={isLoading}
            empty={!isLoading && dailyFlow.every((d) => d.usdIn === 0 && d.payoutsOut === 0)}
          >
            <div className="h-64">
              <DailySeriesChart
                data={dailyFlow}
                xKey="date"
                kind="bar"
                height={256}
                series={[
                  { key: 'usdIn', label: 'Money in', color: CHART_SEMANTIC.moneyIn },
                  { key: 'payoutsOut', label: 'Payouts out', color: CHART_SEMANTIC.moneyOut },
                ]}
                valueFormatter={formatUsd}
              />
            </div>
          </ChartCard>
          <ChartCard
            title="Cash settlement health"
            tooltip="Cash you owe your Agent for credit purchases this period vs cash already paid."
            loading={isLoading}
            empty={!isLoading && cash.expected === 0 && cash.received === 0}
            emptyLabel="No cash settlements in this period"
          >
            <GaugeChart
              percent={cash.collectedPct}
              primaryLabel="Cash paid"
              primaryValue={cash.received}
              secondaryLabel="Still owed"
              secondaryValue={cash.uncollected}
            />
          </ChartCard>
          <ChartCard
            title="Money-in mix"
            tooltip="Period totals for online vs counter deposits and Player money in."
            loading={isLoading}
            empty={!isLoading && activityMix.length === 0}
            emptyLabel="No Player money in this period"
          >
            <CategoryBarChart data={activityMix} color={CHART_SEMANTIC.moneyIn} valueFormatter={formatUsd} />
          </ChartCard>
        </div>
      </Section>
    </div>
  );
}
